/**
 * ⭐⭐ `R2-UI1` · **El popup «Quitar plantilla».** `R2-F2-CA-32`…`CA-34`,
 * `PRD_R2.md:992-994`.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ① POR QUÉ ESTA CONFIRMACIÓN DICE TANTO
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Quitar una plantilla de la lista **no borra nada en Meta y no toca los envíos
 * ya hechos** — pero **sí rompe los envíos PROGRAMADOS** que la usen: esos no van
 * a salir y quedan como fallidos (`PRD_R2.md:992`). Las tres mitades son
 * distintas y el operador no puede adivinar cuál le toca:
 *
 *   · lo que se pierde ....... la plantilla deja de ofrecerse en Nuevo envío;
 *   · lo que se rompe ........ los envíos programados con ella quedan fallidos;
 *   · lo que NO pasa ......... sigue existiendo en Meta; lo ya enviado no cambia.
 *
 * ⛔ **El texto no se abrevia a «¿Seguro?».** Una confirmación que no dice qué
 * pasa es un clic más, no una decisión: el operador la contesta igual y se entera
 * del daño cuando el envío programado no sale.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔ ② EL NOMBRE SE INTERPOLA ACÁ Y NO VIAJA AL SERVIDOR
 * ════════════════════════════════════════════════════════════════════════════
 *
 * El primer párrafo nombra la plantilla, porque una confirmación sobre «la
 * plantilla» con veinte filas a la vista no dice sobre cuál. Eso es distinto del
 * CUERPO DE ERROR del borde, que `contracts/admin-api/http.ts`
 * §`SendListErrorBody` obliga a mantener **fijo y sin ningún valor del pedido**
 * (A7, `ADR-0201`): allá sería una fuga por eco, acá es un dato que el navegador
 * ya tiene dibujado en la fila de al lado.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⭐ ③ ESCAPE ES «CANCELAR», Y NO SE ESCRIBE ACÁ
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Lo da `Dialogo` (`../banco-de-contenido/dialogo.tsx`), que además encierra el
 * foco y lo devuelve al botón que lo abrió. `CA-33` pide que «Cancelar» y Escape
 * dejen la lista igual, y las dos salidas llaman al MISMO `onCancelar`: escribir
 * un segundo manejador de Escape acá dejaría dos caminos que pueden divergir.
 */

import type { JSX } from "react"
import { Dialogo } from "../banco-de-contenido/dialogo.tsx"

export function PopupQuitar({
  nombre,
  sello,
  enCurso,
  error,
  onCancelar,
  onConfirmar,
}: {
  readonly nombre: string
  /** El nombre del brazo, ya armado («KAI · Brazo Híbrido»). */
  readonly sello: string
  readonly enCurso: boolean
  readonly error: string | null
  readonly onCancelar: () => void
  readonly onConfirmar: () => void
}): JSX.Element {
  return (
    <Dialogo titulo="Quitar plantilla" onCerrar={onCancelar}>
      <p>{nombre} sale de la lista de plantillas para envíos.</p>
      <p>
        Afecta solo a {sello}: deja de aparecer en Nuevo envío. Si tiene envíos programados, esos
        envíos no van a salir y quedan como fallidos. La plantilla sigue existiendo en Meta y los
        envíos ya hechos no cambian.
      </p>
      {/*
        ⚠️ El fallo del `DELETE` se dice ADENTRO del popup y el popup NO se cierra.
        Cerrarlo dejaría al operador mirando una lista donde la fila sigue estando,
        sin saber si eso es «no se quitó» o «se quitó y la tabla está vieja».
      */}
      {error !== null && (
        <p className="plantillas__error" role="alert">
          {error}
        </p>
      )}
      <footer className="dialogo__acciones">
        <button type="button" className="boton" onClick={onCancelar}>
          Cancelar
        </button>
        <button
          type="button"
          /*
           * ⭐ `R1.1-F5-CA-95` · el confirmar de una acción destructiva va SÓLIDO
           * en `--peligro`. El «Quitar» de la FILA, que sólo abre esto, se queda en
           * la variante callada: abrir una confirmación no es destructivo.
           */
          className="boton boton--peligro-solido"
          aria-busy={enCurso}
          disabled={enCurso}
          onClick={onConfirmar}
        >
          Sí, quitar plantilla
        </button>
      </footer>
    </Dialogo>
  )
}
